/**
 * IBS group register (P428.1).
 *
 * The consolidation stage between the register and the board: admitted
 * services in a few governable groups. The group's tolerance is DERIVED
 * -- the tightest member's, named -- and never typed here, because a
 * declared group figure is a second number a board could sign that
 * disagrees with the members' own. The reserved Indirect Services group
 * is the product's; a bank creates its customer-exposed groups.
 */
import { useTranslation } from "react-i18next";

import { LinkSection } from "./LinkSection";
import type { AdminColumn, EntityAdapter, FormField } from "./types";

export interface IBSGroupMember {
  ibs_service_id: string;
  id: string;
  code: string;
  name: string;
  mvb_flag: boolean;
  impact_tolerance_hours: number | null;
  since: string;
  rationale: string;
}

export interface IBSGroup {
  id: string;
  code: string;
  name: string;
  description: string | null;
  kind: "customer_exposed" | "indirect_services";
  is_reserved: boolean;
  owner_name: string | null;
  owner_email: string | null;
  review_cadence_days: number | null;
  members: IBSGroupMember[];
  derived_tolerance_hours: number | null;
  derived_tolerance_from: string | null;
  created_at: string;
  updated_at: string;
}

export interface IBSGroupIn {
  code: string;
  name: string;
  description: string | null;
  owner_name: string | null;
  owner_email: string | null;
  review_cadence_days: number | null;
}

/** Changing the owner here is recorded as a handover, server-side. */
export type IBSGroupPatch = Partial<
  Pick<
    IBSGroupIn,
    "name" | "description" | "review_cadence_days" | "owner_name" | "owner_email"
  >
>;

function DerivedTolerance({ row }: { row: IBSGroup }) {
  const { t } = useTranslation();
  if (row.derived_tolerance_hours === null) {
    return (
      <span className="text-xs text-slate-400">
        {t("modules.foundation.admin.ibsGroups.noTolerance")}
      </span>
    );
  }
  return (
    <span
      className="text-xs text-slate-700"
      data-testid={`group-tolerance-${row.code}`}
    >
      {t("modules.foundation.admin.ibsGroups.derivedTolerance", {
        hours: row.derived_tolerance_hours,
        from: row.derived_tolerance_from,
      })}
    </span>
  );
}

const columns: AdminColumn<IBSGroup>[] = [
  {
    key: "name",
    label: "Group",
    sortValue: (r) => r.name,
    render: (r) => (
      <span>
        <span className="font-medium text-slate-900">{r.name}</span>
        <span className="ms-2 font-mono text-xs text-slate-500">
          <span aria-hidden="true"> · </span>
          {r.code}
        </span>
      </span>
    ),
  },
  {
    key: "members",
    label: "Services",
    sortValue: (r) => r.members.length,
    render: (r) => (
      <span className="text-xs text-slate-700 tabular-nums">{r.members.length}</span>
    ),
  },
  {
    key: "derived_tolerance_hours",
    label: "Tolerance (derived)",
    render: (r) => <DerivedTolerance row={r} />,
  },
  {
    key: "owner_name",
    label: "Owner",
    render: (r) =>
      r.owner_name ? (
        <span className="text-xs text-slate-700">{r.owner_name}</span>
      ) : (
        <span className="text-xs text-slate-400">--</span>
      ),
  },
];

const formFields: FormField<IBSGroupIn>[] = [
  // Round 18 G26 -- the code is fixed once the group exists.
  {
    kind: "text",
    key: "code",
    label: "Code",
    required: true,
    maxLength: 64,
    createOnly: true,
  },
  { kind: "text", key: "name", label: "Name", required: true, maxLength: 255 },
  { kind: "textarea", key: "description", label: "Description", rows: 3 },
  { kind: "text", key: "owner_name", label: "Accountable owner", maxLength: 255 },
  { kind: "text", key: "owner_email", label: "Owner email", maxLength: 255 },
];

export function IBSGroupMembers({ rowId }: { rowId: string }) {
  return (
    <LinkSection
      i18nPrefix="modules.foundation.admin.ibsGroups.members"
      listEndpoint={`/v1/admin/foundation/ibs-groups/${rowId}/members`}
      deleteEndpoint={(ibsId) =>
        `/v1/admin/foundation/ibs-groups/${rowId}/members/${ibsId}`
      }
      // Round 17 G20 -- only services in no group yet.
      optionsEndpoint="/v1/admin/foundation/ibs-groups/eligible"
      targetFieldKey="ibs_service_id"
      displayFieldKey="rationale"
      createExtraFieldKey="rationale"
      createExtraRequired
      extraLabelKey="modules.foundation.admin.ibsGroups.members.extraLabel"
      addHintKey="modules.foundation.admin.ibsGroups.members.addHint"
      confirmRemove
      toastKeys={{
        added: "modules.foundation.admin.ibsGroups.members.added",
        removed: "modules.foundation.admin.ibsGroups.members.removed",
      }}
      queryKey={["foundation-admin", "ibs-groups", rowId, "members"] as const}
      optionsQueryKey={["foundation-admin", "ibs-groups", "eligible"] as const}
      alsoInvalidate={[["foundation-admin", "ibs-groups"]]}
    />
  );
}

export const ibsGroupAdapter: EntityAdapter<IBSGroup, IBSGroupIn, IBSGroupPatch> = {
  i18nPrefix: "modules.foundation.admin.ibsGroups",
  createdToastKey: "modules.foundation.admin.ibsGroups.created",
  importKind: "ibs_group",
  endpoint: "/v1/admin/foundation/ibs-groups",
  queryKey: ["foundation-admin", "ibs-groups"] as const,
  columns,
  defaultSort: { key: "name", dir: "asc" },
  rowKey: (r) => r.id,
  rowSearchHaystack: (r) =>
    `${r.code} ${r.name} ${r.owner_name ?? ""} ${r.members.map((m) => m.name).join(" ")}`,
  formFields,
  formInitial: () => ({
    code: "",
    name: "",
    description: null,
    owner_name: null,
    owner_email: null,
    review_cadence_days: null,
  }),
  rowToPatch: (r) => ({
    name: r.name,
    description: r.description,
    review_cadence_days: r.review_cadence_days,
    owner_name: r.owner_name,
    owner_email: r.owner_email,
  }),
  extraSections: [IBSGroupMembers],
};
